import ConsentApiTokenManager from '../lib/ConsentApiTokenManager';
import { networkErrorApiWrapper } from '../lib/NetworkErrorHandler';
import { urnApiWrapper } from '../lib/urnApiWrapper';
import { config } from '../../config';
// @ts-nocheck
export type ThenArg<T> =
  T extends Promise<infer U>
    ? U
    : T extends (...args: any[]) => Promise<infer U>
      ? U
      : T;

type QueryParameters = {
  [key: string]:
    | string
    | number
    | string[]
    | number[]
    | boolean
    | boolean[]
    | undefined;
};

export type ConsentStatus = "granted" | "denied" | "undecided" | "outdated";

export type UserRequestType = "delete" | "export";

export type UserRequestStatus = "pending" | "resolved" | "processing" | "error";

export type UserChangeType = "delete";

export type MeCreateRequestBody = {
  type: "delete" | "export";
  app_data_ids: Array<string>;
};

export type CreateUserRequestBody = {
  type: "delete" | "export";
  client_id: string;
  app_data_ids: Array<string>;
  client_acc_id: string;
};

export type CreateUserRequestResponse = {
  requests: Array<{
    id: string;
    status: string;
  }>;
};

export type CreateConsentTemplatesForAppBody = {
  consents: Array<{
    consent_id: string;
    templates: Array<{
      title: string;
      text: string;
      version: number;
    }>;
  }>;
};

export type CreateAppDataBody = {
  items: Array<{
    id: string;
    title: string;
    description: string;
    delete_support?: boolean;
    export_support?: boolean;
  }>;
  delete_endpoint: string;
  export_endpoint: string;
};

export type SaveUserConsentBody = {
  status: "granted" | "denied";
  version: number;
};

export type AdminSaveUserConsentBody = {
  clientAccId: string;
  clientId: string;
  status: "granted" | "denied";
  version: number;
};

export type ConsentTemplateDetail = {
  consent_id: string;
  title: string;
  text: string;
  version: number;
  inserted_at: string;
  has_more_versions?: boolean;
};

export type ConsentDataDetail = {
  consent_data_id: string;
  type: string;
  description: string;
};

export type CreateConsentTemplatesForAppResponse = {
  consents: Array<ConsentTemplateDetail>;
};

export type AppDataListResponse = {
  items: Array<{
    app_data_id: string;
    title: string;
    description: string;
    delete_url?: string;
    export_url?: string;
  }>;
  delete_endpoint?: AppDataEndpoint;
  export_endpoint?: AppDataEndpoint;
};

export type AppDataDetail = {
  app_data_id: string;
  client_id: string;
  title: string;
  description: string;
  delete_url?: string;
  export_url?: string;
};

export type CreateAppDataResponse = {
  items: Array<{
    app_data_id: string;
    description: string;
  }>;
  delete_endpoint: AppDataEndpoint;
  export_endpoint: AppDataEndpoint;
};

export type AdminAppsResponse = {
  client_ids: Array<string>;
  pagecursor?: Array<string>;
};

export type AdminUserConsentDetail = {
  user_consent_id: string;
  status: ConsentStatus;
  client_id: string;
  consent_id: string;
  consent_title?: string;
  consent_text?: string;
  inserted_at: string;
  version: number;
};

export type UserConsentDetail = {
  user_consent_id: string;
  status: ConsentStatus;
  client_id: string;
  consent_id: string;
  inserted_at: string;
  version: number;
  consent_title: string;
  consent_text: string;
};

export type UserChange = {
  user_change_id: string;
  client_id: string;
  app_data_id: string;
  app_data_title?: string;
  type: UserChangeType;
  inserted_at: string;
};

export type UserConsentStatusDetail = {
  status: ConsentStatus;
  version: number;
};

export type AdminUserConsentsListResponse = {
  data: Array<AdminUserConsentRecord>;
  pagination: {
    limit: number;
    offset: number;
    hasNextOffset: boolean;
  };
};

export type AdminUserConsentRecord = {
  user_id: string;
  consent_category_id: string;
  consent_template_id: string;
  status: ConsentStatus;
  inserted_at: string;
};

export type UserRequest = {
  user_request_id: string;
  client_id: string;
  client_acc_id: string;
  type: UserRequestType;
  status: UserRequestStatus;
  app_data_id: string;
  app_data_title?: string;
  app_data_description?: string;
  inserted_at: string;
  processed_at?: string;
  resolved_at?: string;
  payload?: string;
  callback_url?: string;
};

export type Error = {
  statusCode: number;
  name: string;
  userinfo?: string;
};

export type AppDataEndpoint = {
  client_id: string;
  endpoint: string;
  type: string;
};

class ApiError {
  public message: string;
  public details: Error | null = null;

  constructor(message: string) {
    this.message = message;
  }
}

/**
 * Centralized Identity and Access Management
 * @class ConsentApi
 * @param {(string)} [domainOrOptions] - The project domain.
 */
class ConsentApi {
  protected baseUrl: string = "";
  protected headers: { name: string; value: string }[] = [];
  protected token: string = "";

  serializeQueryParams(parameters: QueryParameters) {
    return Object.keys(parameters)
      .reduce((acc: string[], p) => {
        const param = parameters[p];
        if (typeof param === "undefined" || param === "") {
          return acc;
        }

        if (Array.isArray(parameters[p])) {
          return [
            ...acc,
            ...parameters[p].map(
              (v) => `${encodeURIComponent(p)}=${encodeURIComponent(v)}`,
            ),
          ];
        } else {
          return [
            ...acc,
            `${encodeURIComponent(p)}=${encodeURIComponent(
              String(parameters[p]),
            )}`,
          ];
        }
      }, [])
      .join("&");
  }

  protected transformParameter(value: any, transformOperation?: string) {
    switch (transformOperation) {
      case "joinUsingPipes":
        return Array.isArray(value) ? value.join("|") : value;
      default:
        return value;
    }
  }

  public setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  public setHeaders(headers: { name: string; value: string }[]) {
    this.headers = headers;
  }

  public setToken(token: string) {
    this.token = token;
  }

  protected appendAuthHeaders(headerParams: Headers) {
    const headers = new Headers(headerParams);
    if (this.token) {
      headers.append("Authorization", `Bearer ${this.token}`);
    }
    return headers;
  }

  private async request(
    method: string,
    url: string,
    body: any,
    headers: Headers = new Headers(),
    queryParameters: QueryParameters = {},
  ) {
    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? this.serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? "?" + queryParams : "");

    if (headers.get("Content-Type") === "multipart/form-data") {
      const form = new FormData();
      for (let k in body) {
        const value = body[k];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            form.append(k, v);
          });
        } else {
          form.append(k, value);
        }
      }
      body = form;
    } else if (
      headers.get("Content-Type") === "application/json" &&
      body &&
      Object.keys(body).length > 0
    ) {
      body = JSON.stringify(body);
    } else {
      body = undefined;
    }

    if (headers.get("Content-Type") === "multipart/form-data") {
      headers.delete("Content-Type");
    }

    if (this.headers.length > 0) {
      this.headers.forEach((h) => {
        headers.append(h.name, h.value);
      });
    }

    const response = await fetch(urlWithParams, { method, headers, body });

    if (response.status === 204) return response;

    if (response.ok) {
      const responseContentType =
        (response.headers && response.headers.get("Content-Type")) || "";
      if (responseContentType.includes("application/json")) {
        return response.json();
      }
      return response;
    } else {
      const err = new ApiError(response.statusText);
      err.details = await response.json();
      throw err;
    }
  }

  /**
   * Get all user requests from all applications with optional filtering
   * @method
   * @name ConsentApi#adminGetAllRequests
   */
  adminGetAllRequests(
    parameters: {
      type?: UserRequestType;
      status?: UserRequestStatus;
      clientId?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    requests?: Array<UserRequest>;
    pagecursor?: Array<string>;
  }> {
    let path = "/admin/requests";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["type"] !== "undefined") {
      queryParameters["type"] = parameters["type"];
    }

    queryParameters["type"] = this.transformParameter(queryParameters["type"]);

    if (typeof parameters["status"] !== "undefined") {
      queryParameters["status"] = parameters["status"];
    }

    queryParameters["status"] = this.transformParameter(
      queryParameters["status"],
    );

    if (typeof parameters["clientId"] !== "undefined") {
      queryParameters["clientId"] = parameters["clientId"];
    }

    queryParameters["clientId"] = this.transformParameter(
      queryParameters["clientId"],
    );

    if (typeof parameters["dateFrom"] !== "undefined") {
      queryParameters["dateFrom"] = parameters["dateFrom"];
    }

    queryParameters["dateFrom"] = this.transformParameter(
      queryParameters["dateFrom"],
    );

    if (typeof parameters["dateTo"] !== "undefined") {
      queryParameters["dateTo"] = parameters["dateTo"];
    }

    queryParameters["dateTo"] = this.transformParameter(
      queryParameters["dateTo"],
    );

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get detailed information about a specific user request
   * @method
   * @name ConsentApi#adminGetRequestDetail
   */
  adminGetRequestDetail(
    requestId: string,
    parameters: {} = {},
  ): Promise<UserRequest> {
    let path = "/admin/requests/{requestId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{requestId}", requestId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Update status of a specific user request (processing or resolved) only in delete type request
   * @method
   * @name ConsentApi#adminUpdateRequestStatus
   */
  adminUpdateRequestStatus(
    requestId: string,
    parameters: {} = {},
    body: {
      status: "processing" | "resolved";
    },
  ): Promise<UserRequest> {
    let path = "/admin/requests/{requestId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{requestId}", requestId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get all consent templates for a specific application, ordered by version descending. (Used when exporting consents for the application configuration file)
   * @method
   * @name ConsentApi#adminGetConsentTemplatesForApp
   */
  adminGetConsentTemplatesForApp(
    appId: string,
    parameters: {} = {},
  ): Promise<Array<ConsentTemplateDetail>> {
    let path = "/admin/app/{appId}/consent-templates";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Create consent templates for a specific application (old templates will be removed). (Userd for importing consents from the application configuration file)
   * @method
   * @name ConsentApi#adminCreateConsentTemplates
   */
  adminCreateConsentTemplates(
    appId: string,
    parameters: {} = {},
    body: CreateConsentTemplatesForAppBody,
  ): Promise<CreateConsentTemplatesForAppResponse> {
    let path = "/admin/app/{appId}/consent-templates";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get the latest version of each consent for a specific application.
   * @method
   * @name ConsentApi#adminGetLatestConsentsForApp
   */
  adminGetLatestConsentsForApp(
    appId: string,
    parameters: {} = {},
  ): Promise<Array<ConsentTemplateDetail>> {
    let path = "/admin/app/{appId}/consents";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get a specific consent by ID for a specific application
   * @method
   * @name ConsentApi#adminGetConsentsByIdForApp
   */
  adminGetConsentsByIdForApp(
    appId: string,
    consentId: string,
    parameters: {} = {},
  ): Promise<Array<ConsentTemplateDetail>> {
    let path = "/admin/app/{appId}/consents/{consentId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    path = path.replace("{consentId}", consentId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get all app data for a specific application. (Used for exporting app data for the application configuration file and admin view)
   * @method
   * @name ConsentApi#adminGetAppDataForApp
   */
  adminGetAppDataForApp(
    appId: string,
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<AppDataListResponse> {
    let path = "/admin/app/{appId}/app-data";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Create app data for a specific application (old data will be removed). (Used for importing app data from the application configuration file)
   * @method
   * @name ConsentApi#adminCreateAppData
   */
  adminCreateAppData(
    appId: string,
    parameters: {} = {},
    body: CreateAppDataBody,
  ): Promise<CreateAppDataResponse> {
    let path = "/admin/app/{appId}/app-data";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get detailed information about a specific app data entry for an application
   * @method
   * @name ConsentApi#adminGetAppDataDetail
   */
  adminGetAppDataDetail(
    appId: string,
    appDataId: string,
    parameters: {} = {},
  ): Promise<AppDataDetail> {
    let path = "/admin/app/{appId}/app-data/{appDataId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{appId}", appId.toString());

    path = path.replace("{appDataId}", appDataId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get list of unique client IDs that have app data or consents configured
   * @method
   * @name ConsentApi#adminGetAllApps
   */
  adminGetAllApps(
    parameters: {
      appData?: boolean;
      consents?: boolean;
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<AdminAppsResponse> {
    let path = "/admin/apps";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["appData"] !== "undefined") {
      queryParameters["appData"] = parameters["appData"];
    }

    queryParameters["appData"] = this.transformParameter(
      queryParameters["appData"],
    );

    if (typeof parameters["consents"] !== "undefined") {
      queryParameters["consents"] = parameters["consents"];
    }

    queryParameters["consents"] = this.transformParameter(
      queryParameters["consents"],
    );

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get latest user's consents for his IDs
   * @method
   * @name ConsentApi#adminGetUserConsents
   */
  adminGetUserConsents(parameters: {
    clientAccIds: Array<string>;
    clientId?: string;
    limit?: number;
    pagecursor?: Array<string>;
  }): Promise<{
    consents?: Array<AdminUserConsentDetail>;
    pagecursor?: Array<string>;
  }> {
    let path = "/admin/user/consents";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["clientAccIds"] !== "undefined") {
      queryParameters["clientAccIds"] = parameters["clientAccIds"];
    }

    queryParameters["clientAccIds"] = this.transformParameter(
      queryParameters["clientAccIds"],
    );

    if (typeof parameters["clientAccIds"] === "undefined") {
      throw new Error("Missing required parameter: clientAccIds");
    }

    if (typeof parameters["clientId"] !== "undefined") {
      queryParameters["clientId"] = parameters["clientId"];
    }

    queryParameters["clientId"] = this.transformParameter(
      queryParameters["clientId"],
    );

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get current user consent status for a specific consent and user
   * @method
   * @name ConsentApi#adminGetUserConsentStatus
   */
  adminGetUserConsentStatus(
    consentId: string,
    parameters: {
      clientAccId: string;
      clientId: string;
    },
  ): Promise<UserConsentDetail> {
    let path = "/admin/user/consents/{consentId}/user-status";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{consentId}", consentId.toString());

    if (typeof parameters["clientAccId"] !== "undefined") {
      queryParameters["clientAccId"] = parameters["clientAccId"];
    }

    queryParameters["clientAccId"] = this.transformParameter(
      queryParameters["clientAccId"],
    );

    if (typeof parameters["clientAccId"] === "undefined") {
      throw new Error("Missing required parameter: clientAccId");
    }

    if (typeof parameters["clientId"] !== "undefined") {
      queryParameters["clientId"] = parameters["clientId"];
    }

    queryParameters["clientId"] = this.transformParameter(
      queryParameters["clientId"],
    );

    if (typeof parameters["clientId"] === "undefined") {
      throw new Error("Missing required parameter: clientId");
    }

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Save user consent status for a specific user (auth.presov.sk only)
   * @method
   * @name ConsentApi#adminSaveUserConsent
   */
  adminSaveUserConsent(
    consentId: string,
    parameters: {} = {},
    body: AdminSaveUserConsentBody,
  ): Promise<UserConsentDetail> {
    let path = "/admin/user/consents/{consentId}/user-status";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{consentId}", consentId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get user consent history for a specific consent and client
   * @method
   * @name ConsentApi#adminGetUserConsentHistory
   */
  adminGetUserConsentHistory(
    consentId: string,
    clientId: string,
    parameters: {} = {},
  ): Promise<Array<AdminUserConsentDetail>> {
    let path = "/admin/user/consents/{consentId}/history/{clientId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{consentId}", consentId.toString());

    path = path.replace("{clientId}", clientId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get all changes for specific users
   * @method
   * @name ConsentApi#adminGetUserChanges
   */
  adminGetUserChanges(parameters: {
    clientAccIds: Array<string>;
    clientId?: string;
    limit?: number;
    pagecursor?: Array<string>;
  }): Promise<{
    user_changes?: Array<UserChange>;
    pagecursor?: Array<string>;
  }> {
    let path = "/admin/user/changes";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["clientAccIds"] !== "undefined") {
      queryParameters["clientAccIds"] = parameters["clientAccIds"];
    }

    queryParameters["clientAccIds"] = this.transformParameter(
      queryParameters["clientAccIds"],
    );

    if (typeof parameters["clientAccIds"] === "undefined") {
      throw new Error("Missing required parameter: clientAccIds");
    }

    if (typeof parameters["clientId"] !== "undefined") {
      queryParameters["clientId"] = parameters["clientId"];
    }

    queryParameters["clientId"] = this.transformParameter(
      queryParameters["clientId"],
    );

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get all requests for specific users
   * @method
   * @name ConsentApi#adminGetUserRequests
   */
  adminGetUserRequests(parameters: {
    clientAccIds: Array<string>;
    clientId?: string;
    type?: UserRequestType;
    status?: UserRequestStatus;
    limit?: number;
    pagecursor?: Array<string>;
  }): Promise<{
    user_requests?: Array<UserRequest>;
    pagecursor?: Array<string>;
  }> {
    let path = "/admin/user/requests";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["clientAccIds"] !== "undefined") {
      queryParameters["clientAccIds"] = parameters["clientAccIds"];
    }

    queryParameters["clientAccIds"] = this.transformParameter(
      queryParameters["clientAccIds"],
    );

    if (typeof parameters["clientAccIds"] === "undefined") {
      throw new Error("Missing required parameter: clientAccIds");
    }

    if (typeof parameters["clientId"] !== "undefined") {
      queryParameters["clientId"] = parameters["clientId"];
    }

    queryParameters["clientId"] = this.transformParameter(
      queryParameters["clientId"],
    );

    if (typeof parameters["type"] !== "undefined") {
      queryParameters["type"] = parameters["type"];
    }

    queryParameters["type"] = this.transformParameter(queryParameters["type"]);

    if (typeof parameters["status"] !== "undefined") {
      queryParameters["status"] = parameters["status"];
    }

    queryParameters["status"] = this.transformParameter(
      queryParameters["status"],
    );

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Create a new user request (delete or export)
   * @method
   * @name ConsentApi#adminCreateUserRequest
   */
  adminCreateUserRequest(
    parameters: {} = {},
    body: CreateUserRequestBody,
  ): Promise<CreateUserRequestResponse> {
    let path = "/admin/user/requests";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get the latest version of a specific consent. (For this endpoint, a delegated token must be used).
   * @method
   * @name ConsentApi#publicGetLatestConsentById
   */
  publicGetLatestConsentById(
    consentId: string,
    parameters: {} = {},
  ): Promise<ConsentTemplateDetail> {
    let path = "/consents/{consentId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{consentId}", consentId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get current user consent status for a specific consent
   * @method
   * @name ConsentApi#getUserConsentStatus
   */
  getUserConsentStatus(
    consentId: string,
    parameters: {} = {},
  ): Promise<UserConsentDetail> {
    let path = "/consents/{consentId}/user-status";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{consentId}", consentId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Save user consent status for consent
   * @method
   * @name ConsentApi#saveUserConsent
   */
  saveUserConsent(
    consentId: string,
    parameters: {} = {},
    body: SaveUserConsentBody,
  ): Promise<UserConsentDetail> {
    let path = "/consents/{consentId}/user-status";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{consentId}", consentId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
}

const client = new ConsentApi();
client.setBaseUrl(config.CONSENT_API_URL);
export default ConsentApiTokenManager.authApiProxy(networkErrorApiWrapper(urnApiWrapper(client, 'ConsentApi')));
export { ConsentApi };
